import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api/client";
import useAuth from "../hooks/useAuth";
import { useConfirm } from "../context/ConfirmContext";
import Navbar from "../components/layout/Navbar";
import { geocodeSubscriberAddress } from "../services/geocodingService";

/**
 * Resolve a request's address to coordinates in the browser, using the same
 * hierarchical lookup as clicking a subscriber name on the dispatch map.
 * Returns { latitude, longitude } or null. Best-effort — never throws.
 */
async function geocodeRequestAddress(r) {
  // Prefer the exact location the subscriber pinned on the public form — no
  // need to geocode when we already have a precise pin.
  if (r.latitude != null && r.longitude != null) {
    return { latitude: r.latitude, longitude: r.longitude };
  }
  let fullAddress = r.address || "";
  if (r.village && !fullAddress.toUpperCase().includes(r.village.toUpperCase())) {
    fullAddress = `${fullAddress} ${r.village}`.trim();
  }
  try {
    const geo = await geocodeSubscriberAddress({
      address: fullAddress,
      barangay: r.barangay,
      municipality: r.municipality,
      latitude: null,
      longitude: null,
    });
    if (geo?.success && geo.latitude && geo.longitude) {
      return { latitude: geo.latitude, longitude: geo.longitude };
    }
  } catch { /* best-effort — proceed without coordinates */ }
  return null;
}

/** True when a merge target already has a precise saved location. */
function matchHasSavedLocation(match) {
  return !!(match && match.location_saved && match.latitude != null && match.longitude != null);
}

const STATUS_BADGE = {
  "Pending Review": "bg-yellow-100 text-yellow-800 border-yellow-300",
  "Match Found": "bg-blue-100 text-blue-800 border-blue-300",
  "New Account": "bg-emerald-100 text-emerald-800 border-emerald-300",
  Approved: "bg-green-100 text-green-800 border-green-300",
  Rejected: "bg-red-100 text-red-800 border-red-300",
  Imported: "bg-purple-100 text-purple-800 border-purple-300",
  Cancelled: "bg-gray-200 text-gray-700 border-gray-400",
};

function toErrorMessage(detail, fallback) {
  if (!detail) return fallback || "An error occurred.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => (typeof d === "string" ? d : d?.msg || JSON.stringify(d)))
      .join("; ");
  }
  if (typeof detail === "object") return detail.msg || JSON.stringify(detail);
  return String(detail);
}

function fmtDate(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    return d.toLocaleString();
  } catch {
    return s;
  }
}

function StatusBadge({ status }) {
  const cls = STATUS_BADGE[status] || "bg-gray-100 text-gray-800 border-gray-300";
  return (
    <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded border ${cls}`}>
      {status || "—"}
    </span>
  );
}

function MatchBadge({ match }) {
  if (match) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded border bg-blue-50 border-blue-300 text-blue-800">
        <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
        Existing Account
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded border bg-emerald-50 border-emerald-300 text-emerald-800">
      New
    </span>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-2 py-1.5 border-b border-gray-100 last:border-0 text-sm">
      <div className="text-gray-500">{label}</div>
      <div className="col-span-2 text-gray-900 break-words">{value || <span className="text-gray-400">—</span>}</div>
    </div>
  );
}

function todayStr() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function DetailModal({ open, request, onClose, onUpdated, canDelete, onDelete, canCancel, onCancel }) {
  const [showDispatch, setShowDispatch] = useState(false);
  const [dispatchDate, setDispatchDate] = useState(todayStr());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (request) {
      setShowDispatch(false);
      setDispatchDate(todayStr());
      setErr(null);
    }
  }, [request]);

  if (!open || !request) return null;

  const match = request.existing_match;
  const isImported = request.status === "Imported";
  const isCancelled = request.status === "Cancelled";

  const handleAction = () => {
    setErr(null);
    setShowDispatch(true);
  };

  const confirmDispatch = async () => {
    setSaving(true);
    setErr(null);
    const action = match ? "merge" : "create";
    try {
      const body = { dispatch_date: dispatchDate };
      // Geocode the address so the record carries a precise pin into For
      // Dispatch — unless this is a merge into a record that already has a
      // saved location (then leave its existing coordinates alone).
      if (!matchHasSavedLocation(match)) {
        const coords = await geocodeRequestAddress(request);
        if (coords) {
          body.latitude = coords.latitude;
          body.longitude = coords.longitude;
        }
      }
      const { data } = await api.post(
        `/admin/public-migration-requests/${request.id}/${action}`,
        body,
      );
      onUpdated(data);
      setShowDispatch(false);
    } catch (e) {
      setErr(toErrorMessage(e?.response?.data?.detail, e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 pt-6 sm:pt-10 overflow-y-auto">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-2 sm:mx-4 my-4 sm:my-8 flex flex-col max-h-[90vh]">

          {/* ── Header ── */}
          <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              <h2 className="text-lg font-semibold truncate">
                Public Migration Request — {request.subscriber_name || request.ticket_number}
              </h2>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none ml-2">&times;</button>
          </div>

          {/* ── Body ── */}
          <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">

            <div className="flex flex-wrap gap-2 items-center">
              <StatusBadge status={request.status} />
              <MatchBadge match={match} />
              <span className="text-xs text-gray-500">Submitted {fmtDate(request.created_at)}</span>
            </div>

            {match && (
              <div className="rounded border border-blue-200 bg-blue-50 p-3 text-sm">
                <div className="font-semibold text-blue-900 mb-1">
                  Old Account Number already exists in migration_records
                </div>
                <div className="text-blue-900 grid grid-cols-2 gap-x-4 gap-y-1">
                  <div><span className="text-blue-700">Record ID:</span> {match.id}</div>
                  <div><span className="text-blue-700">Old Acct:</span> {match.oldacctno}</div>
                  <div><span className="text-blue-700">Stage:</span> {match.workflow_stage || "—"}</div>
                  <div><span className="text-blue-700">JO Status:</span> {match.jo_status || "—"}</div>
                  <div><span className="text-blue-700">Team:</span> {match.team_number || "—"}</div>
                  <div><span className="text-blue-700">Date Visited:</span> {match.date_visited || "—"}</div>
                </div>
              </div>
            )}

            {!match && (
              <div className="rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                <div className="font-semibold mb-1">No existing record found</div>
                This Old Account Number was <b>not</b> found in <code>migration_records</code>.
                A new migration record will be created when you proceed.
              </div>
            )}

            {/* ── Subscriber Details ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
              <DetailRow label="Email" value={request.email} />
              <DetailRow label="Email verified" value={fmtDate(request.email_verified_at)} />
              <DetailRow label="Old Account No." value={<span className="font-mono">{request.old_account_number}</span>} />
              <DetailRow label="Name" value={request.subscriber_name} />
              <DetailRow label="Contact" value={request.contact_number} />
              <DetailRow label="Municipality" value={request.municipality} />
              <DetailRow label="Barangay" value={request.barangay} />
              <DetailRow label="Address" value={request.address} />
              <DetailRow label="Landmark / Instructions" value={request.landmark} />
              <DetailRow label="Condo Unit" value={request.is_condo === true ? "Yes" : request.is_condo === false ? "No" : null} />
              <DetailRow label="Bundle Type" value={request.bundle_type} />
              <DetailRow label="IP" value={request.ip_address} />
            </div>

            {isImported && (
              <div className="text-sm text-green-700 bg-green-50 rounded px-3 py-2">
                This request has been imported into the migration system.
              </div>
            )}
          </div>

          {/* ── Error bar ── */}
          {err && (
            <div className="px-4 sm:px-6 py-2 shrink-0">
              <p className="text-sm text-red-700 bg-red-50 rounded px-3 py-2">{err}</p>
            </div>
          )}

          {/* ── Footer action buttons ── */}
          <div className="sticky bottom-0 flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-t bg-white shrink-0 z-10">
            <div className="flex gap-2">
              {canDelete && (
                <button
                  onClick={() => onDelete(request)}
                  disabled={saving}
                  className="px-4 py-2 text-sm rounded text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                >
                  Delete
                </button>
              )}
              {canCancel && !isImported && !isCancelled && (
                <button
                  onClick={() => onCancel(request)}
                  disabled={saving}
                  className="px-4 py-2 text-sm rounded text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
                >
                  Cancel Request
                </button>
              )}
            </div>
            <div className="flex gap-2">
              {!isImported && !isCancelled && (
                <button
                  onClick={handleAction}
                  disabled={saving}
                  className={`px-4 py-2 text-sm rounded text-white disabled:opacity-50 ${
                    match
                      ? "bg-blue-600 hover:bg-blue-700"
                      : "bg-emerald-600 hover:bg-emerald-700"
                  }`}
                >
                  {match ? "Merge Record" : "Create Record"}
                </button>
              )}
              <button onClick={onClose} className="px-4 py-2 text-sm border rounded hover:bg-gray-100">Close</button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Dispatch date confirmation dialog ── */}
      {showDispatch && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-lg font-semibold mb-2">Dispatch Date</h3>
            <p className="text-sm text-gray-600 mb-4">
              {match
                ? "Set the dispatch date and confirm to merge this request into the existing record."
                : "Set the dispatch date and confirm to create a new migration record."}
            </p>
            <input
              type="date"
              value={dispatchDate}
              onChange={(e) => setDispatchDate(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm mb-4"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowDispatch(false)}
                className="px-4 py-2 text-sm border rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={confirmDispatch}
                disabled={saving || !dispatchDate}
                className="px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? "Processing…" : "Confirm Move"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function PublicMigrationRequestsPage() {
  const { hasPerm } = useAuth();
  const { confirm } = useConfirm();
  const canDelete = hasPerm("public_migration.delete");
  const canCancel = hasPerm("public_migration.cancel");
  const canBulk = hasPerm("public_migration.bulk_action");

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0 });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [matchFilter, setMatchFilter] = useState("");
  const [municipalityFilter, setMunicipalityFilter] = useState("");
  const [brgyFilter, setBrgyFilter] = useState("");
  const [sduMduFilter, setSduMduFilter] = useState("");
  const [bundleFilter, setBundleFilter] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDialog, setBulkDialog] = useState(null); // { mode: "merge"|"create" }
  const [bulkDate, setBulkDate] = useState(todayStr());
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkGeo, setBulkGeo] = useState(null); // { done, total } during geocoding
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      const { data } = await api.get("/admin/public-migration-requests", { params });
      setItems(data.items || []);
      setStats(data.stats || { total: 0, pending: 0 });
      setSelectedIds(new Set());
    } catch (e) {
      setErr(toErrorMessage(e?.response?.data?.detail, e.message));
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const filterOptions = useMemo(() => {
    const unique = (fn) => [...new Set(items.map(fn).filter(Boolean))].sort();
    const brgySource = municipalityFilter
      ? items.filter((r) => r.municipality === municipalityFilter)
      : items;
    return {
      municipalities: unique((r) => r.municipality),
      brgys: [...new Set(brgySource.map((r) => r.barangay).filter(Boolean))].sort(),
      sduMdus: unique((r) => r.is_condo === true ? "MDU" : r.is_condo === false ? "SDU" : ""),
      bundles: unique((r) => r.bundle_type),
      statuses: unique((r) => r.status),
    };
  }, [items, municipalityFilter]);

  const filteredItems = useMemo(() => {
    let result = items;
    if (statusFilter) result = result.filter((r) => r.status === statusFilter);
    if (matchFilter === "matched") result = result.filter((r) => !!r.existing_match);
    if (matchFilter === "new") result = result.filter((r) => !r.existing_match);
    if (municipalityFilter) result = result.filter((r) => r.municipality === municipalityFilter);
    if (brgyFilter) result = result.filter((r) => r.barangay === brgyFilter);
    if (sduMduFilter) result = result.filter((r) => {
      const val = r.is_condo === true ? "MDU" : r.is_condo === false ? "SDU" : "";
      return val === sduMduFilter;
    });
    if (bundleFilter) result = result.filter((r) => r.bundle_type === bundleFilter);
    return result;
  }, [items, statusFilter, matchFilter, municipalityFilter, brgyFilter, sduMduFilter, bundleFilter]);

  const onUpdated = (updated) => {
    setItems((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    setSelected(updated);
  };

  // Bulk selection bookkeeping
  const selectableRows = useMemo(
    () => filteredItems.filter((r) => r.status === "Pending Review"),
    [filteredItems],
  );
  const selectedRows = useMemo(
    () => filteredItems.filter((r) => selectedIds.has(r.id)),
    [filteredItems, selectedIds],
  );
  const allSelectableSelected =
    selectableRows.length > 0 &&
    selectableRows.every((r) => selectedIds.has(r.id));

  const bulkMode = useMemo(() => {
    if (!canBulk || selectedRows.length === 0) return null;
    if (selectedRows.some((r) => r.status !== "Pending Review")) return null;
    const hasExisting = selectedRows.some((r) => !!r.existing_match);
    const hasNew = selectedRows.some((r) => !r.existing_match);
    if (hasExisting && hasNew) return null;
    return hasExisting ? "merge" : "create";
  }, [canBulk, selectedRows]);

  const toggleRow = (id, status) => {
    if (status !== "Pending Review") return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds((prev) => {
      if (selectableRows.every((r) => prev.has(r.id))) return new Set();
      return new Set(selectableRows.map((r) => r.id));
    });
  };

  const openBulkDialog = (mode) => {
    setBulkDate(todayStr());
    setErr(null);
    setBulkDialog({ mode });
  };

  const confirmBulk = async () => {
    if (!bulkDialog) return;
    setBulkSaving(true);
    setErr(null);
    try {
      // Geocode addresses in the browser before submitting so each record
      // carries a precise pin into For Dispatch. For "create" every selected
      // request needs it; for "merge" only those whose existing record has no
      // saved location yet (existing coordinates are never overwritten).
      const rowsToGeocode = selectedRows.filter(
        (r) => bulkDialog.mode === "create" || !matchHasSavedLocation(r.existing_match),
      );
      const coordinates = {};
      let done = 0;
      setBulkGeo({ done: 0, total: rowsToGeocode.length });

      const CONCURRENCY = 5;
      let idx = 0;
      const worker = async () => {
        while (idx < rowsToGeocode.length) {
          const r = rowsToGeocode[idx++];
          const coords = await geocodeRequestAddress(r);
          if (coords) coordinates[r.id] = coords;
          done += 1;
          setBulkGeo({ done, total: rowsToGeocode.length });
        }
      };
      await Promise.all(
        Array.from({ length: Math.min(CONCURRENCY, rowsToGeocode.length || 1) }, worker),
      );

      const url = bulkDialog.mode === "merge"
        ? "/admin/public-migration-requests/bulk-merge"
        : "/admin/public-migration-requests/bulk-create";
      await api.post(url, {
        request_ids: Array.from(selectedIds),
        dispatch_date: bulkDate,
        coordinates,
      });
      setBulkDialog(null);
      setSelectedIds(new Set());
      load();
    } catch (e) {
      setErr(toErrorMessage(e?.response?.data?.detail, "Bulk action failed."));
    } finally {
      setBulkSaving(false);
      setBulkGeo(null);
    }
  };

  const handleDelete = async (request) => {
    const confirmed = await confirm({
      title: "Delete Request",
      message: `Delete "${request.ticket_number}" (${request.subscriber_name || request.email})? This cannot be undone.`,
      confirmLabel: "Delete",
      variant: "danger",
    });
    if (!confirmed) return;
    try {
      await api.delete(`/admin/public-migration-requests/${request.id}`);
      setSelected(null);
      load();
    } catch (e) {
      setErr(toErrorMessage(e?.response?.data?.detail, "Failed to delete request."));
    }
  };

  const handleCancel = async (request) => {
    const confirmed = await confirm({
      title: "Cancel Request",
      message: `Cancel "${request.ticket_number}" (${request.subscriber_name || request.email})? The request will be marked as Cancelled.`,
      confirmLabel: "Cancel Request",
      variant: "danger",
    });
    if (!confirmed) return;
    try {
      const { data } = await api.post(`/admin/public-migration-requests/${request.id}/cancel`);
      onUpdated(data);
    } catch (e) {
      setErr(toErrorMessage(e?.response?.data?.detail, "Failed to cancel request."));
    }
  };

  const SORT_ACCESSORS = {
    ticket: (r) => r.ticket_number || "",
    submitted: (r) => r.created_at || "",
    email: (r) => r.email || "",
    account: (r) => r.old_account_number || "",
    name: (r) => r.subscriber_name || "",
    brgy: (r) => r.barangay || "",
    municipality: (r) => r.municipality || "",
    sdu_mdu: (r) => (r.is_condo === true ? "MDU" : r.is_condo === false ? "SDU" : ""),
    address: (r) => r.address || "",
    bundle: (r) => r.bundle_type || "",
    db_match: (r) => (r.existing_match ? 1 : 0),
    status: (r) => r.status || "",
  };

  const sortedItems = useMemo(() => {
    if (!sortKey || !SORT_ACCESSORS[sortKey]) return filteredItems;
    const accessor = SORT_ACCESSORS[sortKey];
    const dir = sortDir === "desc" ? -1 : 1;
    const arr = [...filteredItems];
    arr.sort((a, b) => {
      const va = accessor(a);
      const vb = accessor(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: "base" }) * dir;
    });
    return arr;
  }, [filteredItems, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const SortHeader = ({ k, children, className = "" }) => {
    const active = sortKey === k;
    const arrow = active ? (sortDir === "asc" ? "▲" : "▼") : "";
    return (
      <th
        onClick={() => handleSort(k)}
        className={`px-3 py-2 font-semibold cursor-pointer select-none hover:bg-gray-100 ${className}`}
      >
        <span className="inline-flex items-center gap-1">
          {children}
          <span className={`text-[10px] ${active ? "text-gray-700" : "text-gray-300"}`}>
            {arrow || "↕"}
          </span>
        </span>
      </th>
    );
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-hidden">
      <Navbar />
      <div className="flex-1 min-h-0 flex flex-col p-4 sm:p-6 w-full">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3 flex-shrink-0">
          <div className="flex flex-col sm:flex-row gap-2 flex-wrap items-start sm:items-center">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search ticket / name / email / acct no."
              className="border rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-56"
            />
            <select
              value={municipalityFilter}
              onChange={(e) => { setMunicipalityFilter(e.target.value); setBrgyFilter(""); }}
              className="border rounded px-2 py-1.5 text-sm"
            >
              <option value="">All Municipalities</option>
              {filterOptions.municipalities.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
            <select
              value={brgyFilter}
              onChange={(e) => setBrgyFilter(e.target.value)}
              className="border rounded px-2 py-1.5 text-sm"
            >
              <option value="">All Barangays</option>
              {filterOptions.brgys.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
            <select
              value={sduMduFilter}
              onChange={(e) => setSduMduFilter(e.target.value)}
              className="border rounded px-2 py-1.5 text-sm"
            >
              <option value="">All SDU/MDU</option>
              {filterOptions.sduMdus.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
            <select
              value={bundleFilter}
              onChange={(e) => setBundleFilter(e.target.value)}
              className="border rounded px-2 py-1.5 text-sm"
            >
              <option value="">All Bundles</option>
              {filterOptions.bundles.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
            <select
              value={matchFilter}
              onChange={(e) => setMatchFilter(e.target.value)}
              className="border rounded px-2 py-1.5 text-sm"
            >
              <option value="">All DB Match</option>
              <option value="matched">Existing Account</option>
              <option value="new">New</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border rounded px-2 py-1.5 text-sm"
            >
              <option value="">All Statuses</option>
              {filterOptions.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <button
              onClick={load}
              className="px-3 py-1.5 text-sm border rounded bg-blue-600 text-white hover:bg-blue-700"
            >
              Refresh
            </button>
            {canBulk && bulkMode === "merge" && (
              <button
                onClick={() => openBulkDialog("merge")}
                className="px-3 py-1.5 text-sm border rounded bg-blue-600 text-white hover:bg-blue-700"
              >
                Merge Record(s) ({selectedRows.length})
              </button>
            )}
            {canBulk && bulkMode === "create" && (
              <button
                onClick={() => openBulkDialog("create")}
                className="px-3 py-1.5 text-sm border rounded bg-emerald-600 text-white hover:bg-emerald-700"
              >
                Create Record(s) ({selectedRows.length})
              </button>
            )}
          </div>
          <div className="flex gap-3 text-sm flex-shrink-0">
            <div className="bg-white border rounded px-3 py-1">
              <span className="text-gray-500 mr-1">Total</span>
              <span className="font-bold text-gray-900">{stats.total}</span>
            </div>
            <div className="bg-white border rounded px-3 py-1">
              <span className="text-gray-500 mr-1">Pending Review</span>
              <span className="font-bold text-yellow-700">{stats.pending}</span>
            </div>
          </div>
        </div>

        <h1 className="text-xl font-bold text-gray-900 mb-3">Public Migration Requests</h1>

        {err && <div className="text-sm text-red-600 mb-3">{err}</div>}

        <div className="bg-white border rounded flex-1 min-h-0 flex flex-col overflow-hidden">
          <div className="flex-1 min-h-0 overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-700 text-left sticky top-0 z-10">
                <tr>
                  {canBulk && (
                    <th className="px-3 py-2 w-10">
                      <input
                        type="checkbox"
                        checked={allSelectableSelected}
                        onChange={toggleAll}
                        disabled={selectableRows.length === 0}
                      />
                    </th>
                  )}
                  <th className="px-3 py-2 font-semibold w-10 text-center">#</th>
                  <SortHeader k="ticket">Ticket</SortHeader>
                  <SortHeader k="submitted">Submitted</SortHeader>
                  <SortHeader k="email">Email</SortHeader>
                  <SortHeader k="account">Account No.</SortHeader>
                  <SortHeader k="name">Name</SortHeader>
                  <SortHeader k="brgy">Brgy</SortHeader>
                  <SortHeader k="municipality">Municipality</SortHeader>
                  <SortHeader k="sdu_mdu">SDU/MDU</SortHeader>
                  <SortHeader k="address">Address</SortHeader>
                  <SortHeader k="bundle">Bundle</SortHeader>
                  <SortHeader k="db_match">DB Match</SortHeader>
                  <SortHeader k="status">Status</SortHeader>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={canBulk ? 14 : 13} className="px-3 py-6 text-center text-gray-500">Loading…</td></tr>
                )}
                {!loading && filteredItems.length === 0 && (
                  <tr><td colSpan={canBulk ? 14 : 13} className="px-3 py-6 text-center text-gray-500">No requests found.</td></tr>
                )}
                {!loading && sortedItems.map((r, idx) => (
                  <tr
                    key={r.id}
                    onClick={() => setSelected(r)}
                    className="border-t border-gray-100 hover:bg-blue-50 cursor-pointer"
                  >
                    {canBulk && (
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(r.id)}
                          disabled={r.status !== "Pending Review"}
                          onChange={() => toggleRow(r.id, r.status)}
                        />
                      </td>
                    )}
                    <td className="px-3 py-2 text-center text-gray-500">{idx + 1}</td>
                    <td className="px-3 py-2 font-mono text-blue-700">{r.ticket_number}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                    <td className="px-3 py-2">{r.email}</td>
                    <td className="px-3 py-2 font-mono">{r.old_account_number}</td>
                    <td className="px-3 py-2">{r.subscriber_name}</td>
                    <td className="px-3 py-2">{r.barangay}</td>
                    <td className="px-3 py-2">{r.municipality}</td>
                    <td className="px-3 py-2">{r.is_condo === true ? "MDU" : r.is_condo === false ? "SDU" : ""}</td>
                    <td className="px-3 py-2">{r.address}</td>
                    <td className="px-3 py-2">{r.bundle_type}</td>
                    <td className="px-3 py-2"><MatchBadge match={r.existing_match} /></td>
                    <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center px-3 py-2 border-t border-gray-200 text-sm bg-gray-50">
            <div className="text-gray-600">{filteredItems.length} records</div>
          </div>
        </div>
      </div>

      <DetailModal
        open={!!selected}
        request={selected}
        onClose={() => setSelected(null)}
        onUpdated={onUpdated}
        canDelete={canDelete}
        onDelete={handleDelete}
        canCancel={canCancel}
        onCancel={handleCancel}
      />

      {bulkDialog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-lg font-semibold mb-2">
              {bulkDialog.mode === "merge" ? "Merge Records" : "Create Records"}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Set the dispatch date for the {selectedRows.length} selected request(s). All records will use the same dispatch date.
            </p>
            <input
              type="date"
              value={bulkDate}
              onChange={(e) => setBulkDate(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm mb-4"
            />
            {bulkGeo && bulkGeo.total > 0 && (
              <p className="text-xs text-blue-600 mb-3">
                Locating addresses… {bulkGeo.done}/{bulkGeo.total}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setBulkDialog(null)}
                className="px-4 py-2 text-sm border rounded hover:bg-gray-100"
                disabled={bulkSaving}
              >
                Cancel
              </button>
              <button
                onClick={confirmBulk}
                disabled={bulkSaving || !bulkDate}
                className={`px-4 py-2 text-sm rounded text-white disabled:opacity-50 ${
                  bulkDialog.mode === "merge"
                    ? "bg-blue-600 hover:bg-blue-700"
                    : "bg-emerald-600 hover:bg-emerald-700"
                }`}
              >
                {bulkSaving ? (bulkGeo ? "Locating…" : "Processing…") : (bulkDialog.mode === "merge" ? "Confirm Merge" : "Confirm Create")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
